import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BUSINESS, TEL_HREF, WA_HREF, MEDIA } from "../../lib/business";
import { Button } from "../ui/button";

const NAV = [
  { id: "services", label: "Services" },
  { id: "about", label: "About" },
  { id: "certifications", label: "Certifications" },
  { id: "machinery", label: "Machinery", route: "/machinery" },
  { id: "location", label: "Location" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

export const Header = ({ onQuoteClick }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (item) => {
    setOpen(false);
    if (item.route) {
      navigate(item.route);
      window.scrollTo({ top: 0, behavior: "instant" });
      return;
    }
    const id = item.id;
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    if (id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#E5E5E5]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => go({ id: "top" })}
          className="flex items-center gap-2 group"
          aria-label="White Bull Global home"
        >
          <img
            src={MEDIA.logoWide}
            alt="White Bull Global"
            className="h-9 md:h-11 w-auto object-contain"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => go(n)}
              className="text-sm text-[#2A2A2A] hover:text-[#D4AF37] transition-colors tracking-wide"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            data-testid="header-call"
            href={TEL_HREF}
            className="inline-flex items-center gap-2 text-sm text-[#1A1A1A] hover:text-[#D4AF37] transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span className="tracking-wide">{BUSINESS.phoneDisplay}</span>
          </a>
          <Button
            data-testid="header-get-quote"
            onClick={onQuoteClick}
            className="btn-gold rounded-sm h-10 px-5 text-sm font-semibold"
          >
            Get Quote
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-[#1A1A1A]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[#E5E5E5] bg-white"
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`mobile-nav-${n.id}`}
                onClick={() => go(n)}
                className="text-left py-3 text-[#1A1A1A] border-b border-[#F0F0F0]"
              >
                {n.label}
              </button>
            ))}
            <div className="flex gap-2 pt-4">
              <a
                href={TEL_HREF}
                data-testid="mobile-call"
                className="flex-1 inline-flex items-center justify-center gap-2 h-11 border border-[#1A1A1A] text-[#1A1A1A] text-sm"
              >
                <Phone className="w-4 h-4" /> Call
              </a>
              <a
                href={WA_HREF}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-whatsapp"
                className="flex-1 inline-flex items-center justify-center gap-2 h-11 btn-gold text-sm"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
            <Button
              data-testid="mobile-get-quote"
              onClick={() => {
                setOpen(false);
                onQuoteClick();
              }}
              className="btn-gold rounded-sm mt-3 h-11 text-sm font-semibold"
            >
              Get Quote
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
